"use client";

import Script from "next/script";
import { useEffect, useState } from "react";
import { GA_TRACKING_ID, GADS_TRACKING_ID } from "@/app/utils/gtag";

const isEnabled = process.env.NEXT_PUBLIC_GOOGLE_TAG_ENABLED !== "false";
/**
 * Defer until first interaction by default (mobile TBT).
 * Set NEXT_PUBLIC_GOOGLE_TAG_DEFER_UNTIL_INTERACTION=false for Tag Assistant / early detection.
 */
const deferUntilInteraction =
	process.env.NEXT_PUBLIC_GOOGLE_TAG_DEFER_UNTIL_INTERACTION !== "false";

function configureGtag() {
	const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
	if (typeof gtag !== "function") return;
	gtag("js", new Date());
	gtag("config", GA_TRACKING_ID);
	gtag("config", `AW-${GADS_TRACKING_ID}`);
}

/**
 * Loads gtag.js after first user interaction by default (lazyOnload fallback when not deferred).
 * Queued dataLayer calls still work via the inline stub in layout before the external script loads.
 */
export default function GoogleAnalyticsTag() {
	const [shouldLoad, setShouldLoad] = useState(!deferUntilInteraction);

	useEffect(() => {
		if (!isEnabled || !deferUntilInteraction) return;

		const enable = () => setShouldLoad(true);
		const events = ["mousedown", "keydown", "touchstart", "scroll"] as const;

		events.forEach((event) =>
			window.addEventListener(event, enable, { once: true, passive: true }),
		);

		return () => {
			events.forEach((event) => window.removeEventListener(event, enable));
		};
	}, []);

	if (!isEnabled || !shouldLoad) return null;

	return (
		<>
			<Script
				src={`https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`}
				strategy="lazyOnload"
				onLoad={configureGtag}
			/>
		</>
	);
}
